import {
  CATALOG_EMBED_RUNTIME_ATTR_KEYS,
  type CompiledTemplateJson,
  isCatalogEmbedNodeType,
} from "@repo/shared";
import type { JSONContent } from "@tiptap/react";

import {
  buildCompiledSchemaRegistry,
  type DocumentEntityType,
  type DocumentSchemaConfig,
  resolveRuntimeEntityTabMap,
} from "./extensions/schemas";
import type { DocumentJsonMap } from "./hooks/use-document-persistence";
import { isMeaningfulAtomNodeType } from "./meaningful-atom-types";

const CLARIFICATION_ANCHOR_MARK_TYPE = "clarificationAnchor";
const CATALOG_RUNTIME_ATTR_KEYS = new Set<string>(CATALOG_EMBED_RUNTIME_ATTR_KEYS);

export interface CanonicalizeRichTextOptions {
  stripClarificationAnchorMarks?: boolean;
  stripBlockIds?: boolean;
  stripMentionSuggestionChar?: boolean;
  stripOrderedListDefaults?: boolean;
  stripEmptyTopLevelFields?: boolean;
  stripEmptyTabs?: boolean;
  entityType?: DocumentEntityType;
  frameworkEntityTypeKey?: string | null;
  compiledTemplateJson?: CompiledTemplateJson | null;
}

type NormalizeNodeResult = {
  node: JSONContent;
  changed: boolean;
};

function normalizeNodeAttrs(
  attrs: Record<string, unknown> | undefined,
  nodeType: string | undefined,
  options: CanonicalizeRichTextOptions
): { attrs?: Record<string, unknown>; changed: boolean } {
  if (!attrs) {
    return { attrs: undefined, changed: false };
  }

  let changed = false;
  const nextAttrs: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined) {
      changed = true;
      continue;
    }

    if (key === "_diffStatus" || key === "_diffOldValue") {
      changed = true;
      continue;
    }

    if (
      key === "required" ||
      key === "guidance" ||
      key === "assistantGuidance" ||
      key === "expectedMentionTypes"
    ) {
      changed = true;
      continue;
    }

    // `chromeless` is a section's schema saying how to *draw* it, not something an author wrote.
    // The hydration pass states the same rule from the other side — a field that is not
    // chromeless carries no `chromeless` attr at all (`schema-metadata.ts`) — but a mounted
    // editor re-serializes the node's attribute default, so its JSON says `chromeless: false`
    // where every stored document says nothing. Two shapes that can never compare equal.
    if (key === "chromeless" && value !== true) {
      changed = true;
      continue;
    }

    if (isCatalogEmbedNodeType(nodeType) && CATALOG_RUNTIME_ATTR_KEYS.has(key)) {
      changed = true;
      continue;
    }

    // Structural editor block ids are volatile, but a documentEvidence blockId is
    // immutable source identity. Stripping it would make two chips citing different
    // blocks of the same document compare equal, so a re-citation would read as clean
    // and never save.
    if (key === "blockId" && options.stripBlockIds && nodeType !== "documentEvidence") {
      changed = true;
      continue;
    }

    if (key === "mentionSuggestionChar" && options.stripMentionSuggestionChar) {
      changed = true;
      continue;
    }

    if (options.stripOrderedListDefaults && nodeType === "orderedList") {
      if (key === "start" && value === 1) {
        changed = true;
        continue;
      }

      if (key === "type" && value === null) {
        changed = true;
        continue;
      }
    }

    nextAttrs[key] = value;
  }

  if (nodeType === "mention" && nextAttrs.mentionSuggestionChar !== undefined) {
    nextAttrs.mentionSuggestionChar = "@";
  }

  return {
    attrs: Object.keys(nextAttrs).length > 0 ? nextAttrs : undefined,
    changed,
  };
}

function canonicalizeRichTextNode(
  node: JSONContent,
  options: CanonicalizeRichTextOptions
): NormalizeNodeResult {
  let changed = false;

  const { attrs: nextAttrs, changed: attrsChanged } = normalizeNodeAttrs(
    node.attrs as Record<string, unknown> | undefined,
    node.type,
    options
  );
  if (attrsChanged) {
    changed = true;
  }

  let nextMarks = node.marks;
  if (options.stripClarificationAnchorMarks && Array.isArray(node.marks)) {
    const filteredMarks = node.marks.filter(
      (mark) => mark?.type !== CLARIFICATION_ANCHOR_MARK_TYPE
    );
    if (filteredMarks.length !== node.marks.length) {
      nextMarks = filteredMarks.length > 0 ? filteredMarks : undefined;
      changed = true;
    }
  }

  let nextContent = node.content;
  if (Array.isArray(node.content)) {
    let contentChanged = false;
    const normalizedContent = node.content.map((child) => {
      const result = canonicalizeRichTextNode(child, options);
      if (result.changed) {
        contentChanged = true;
      }
      return result.node;
    });

    if (contentChanged) {
      nextContent = normalizedContent;
      changed = true;
    }
  }

  if (!changed) {
    return { node, changed: false };
  }

  const nextNode: JSONContent = { ...node };
  if (nextAttrs) {
    nextNode.attrs = nextAttrs;
  } else {
    delete (nextNode as { attrs?: unknown }).attrs;
  }

  if (nextMarks) {
    nextNode.marks = nextMarks;
  } else {
    delete (nextNode as { marks?: unknown }).marks;
  }

  if (nextContent) {
    nextNode.content = nextContent;
  }

  return { node: nextNode, changed: true };
}

export function canonicalizeRichTextDocument(
  document: JSONContent,
  options: CanonicalizeRichTextOptions = {}
): JSONContent {
  return canonicalizeRichTextNode(document, options).node;
}

function resolveFieldId(
  schemaConfig: DocumentSchemaConfig,
  node: JSONContent,
  index: number
): string | null {
  const explicitFieldId =
    typeof node.attrs?.fieldId === "string" && node.attrs.fieldId.length > 0
      ? node.attrs.fieldId
      : null;
  if (explicitFieldId) {
    return explicitFieldId;
  }

  const positionalField = schemaConfig.fields[index];
  if (positionalField && positionalField.nodeType === node.type) {
    return positionalField.fieldId;
  }

  return null;
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function nodeHasMeaningfulContent(node: JSONContent | null | undefined): boolean {
  if (!node) return false;

  if (typeof node.text === "string" && node.text.trim().length > 0) {
    return true;
  }

  // Emptiness here is decided per field elsewhere (`isEmptyFieldNode`), so an
  // `atomicField` counts as content regardless of its value.
  if (isMeaningfulAtomNodeType(node.type) || node.type === "atomicField") {
    return true;
  }

  if (!Array.isArray(node.content)) {
    return false;
  }

  return node.content.some((child) => nodeHasMeaningfulContent(child));
}

function isTopLevelFieldNodeEmpty(
  schemaConfig: DocumentSchemaConfig,
  node: JSONContent,
  index: number
): boolean {
  const fieldId = resolveFieldId(schemaConfig, node, index);
  if (!fieldId) {
    return false;
  }

  switch (node.type) {
    case "atomicField":
      return !hasMeaningfulValue(node.attrs?.value);
    case "sectionTitle":
    case "paragraphSection":
    case "richSection":
      return !nodeHasMeaningfulContent(node);
    default:
      return false;
  }
}

function pruneEmptyTopLevelFields(
  document: JSONContent,
  schemaConfig: DocumentSchemaConfig
): JSONContent {
  if (!Array.isArray(document.content)) {
    return document;
  }

  const nextContent = document.content.filter(
    (node, index) => !isTopLevelFieldNodeEmpty(schemaConfig, node, index)
  );

  if (nextContent.length === document.content.length) {
    return document;
  }

  return {
    ...document,
    content: nextContent,
  };
}

export function canonicalizeRichTextTabDocument(
  document: JSONContent,
  options: CanonicalizeRichTextOptions & {
    schemaConfig?: DocumentSchemaConfig | null;
  } = {}
): JSONContent {
  const canonicalDocument = canonicalizeRichTextDocument(document, options);

  if (!options.stripEmptyTopLevelFields || !options.schemaConfig) {
    return canonicalDocument;
  }

  return pruneEmptyTopLevelFields(canonicalDocument, options.schemaConfig);
}

export function canonicalizeRichTextDocumentMap(
  documentJson: DocumentJsonMap,
  options: CanonicalizeRichTextOptions = {}
): DocumentJsonMap {
  const compiledSchemaRegistry = buildCompiledSchemaRegistry(options.compiledTemplateJson);
  const tabMap =
    options.entityType !== undefined
      ? resolveRuntimeEntityTabMap(options.entityType, {
          frameworkEntityTypeKey: options.frameworkEntityTypeKey,
          compiledSchemaRegistry,
        })
      : null;

  const nextEntries = Object.entries(documentJson)
    .map(([tabKey, doc]) => {
      const schemaConfig = tabMap?.[tabKey];
      const canonicalDoc = canonicalizeRichTextTabDocument(doc, {
        ...options,
        schemaConfig,
      });
      return [tabKey, canonicalDoc] as const;
    })
    .filter(
      ([, doc]) => !options.stripEmptyTabs || (Array.isArray(doc.content) && doc.content.length > 0)
    );

  return Object.fromEntries(nextEntries);
}
